import React, { useEffect, useRef, useState } from "react";
import {
  Card, Button, Table, Alert, Modal, Form, Badge, Spinner
} from "@themesberg/react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faTrash, faEdit } from "@fortawesome/free-solid-svg-icons";
import api from "../../utils/api";
import { uploadToR2, generateUUID } from "../../utils/r2Upload";

export default () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [mediaType, setMediaType] = useState("text");
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const dragItem = useRef();
  const dragOverItem = useRef();

  const load = async () => {
    try {
      setLoading(true);
      const res = await api.get("/auto-msg/templates");
      setTemplates(res.data?.templates || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditing(null);
    setTitle("");
    setText("");
    setMediaType("text");
    setFile(null);
    setShowModal(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setTitle(t.title || "");
    setText(t.text || "");
    setMediaType(t.mediaType || "text");
    setFile(null);
    setShowModal(true);
  };

  const save = async () => {
    if (!title.trim()) {
      setError("Başlık gerekli.");
      return;
    }
    try {
      setSaving(true);
      let mediaUrl = editing?.mediaUrl || null;
      let thumbnailUrl = editing?.thumbnailUrl || null;
      let type = mediaType;

      if (file) {
        mediaUrl = await uploadToR2(file, `message-templates/${generateUUID()}_${file.name}`);
        if (file.type.startsWith("image/")) type = "image";
        else if (file.type.startsWith("video/")) type = "video";
        else if (file.type.startsWith("audio/")) type = "audio";
      }

      const payload = { title: title.trim(), text, mediaType: type, mediaUrl, thumbnailUrl };
      if (editing) {
        await api.put(`/auto-msg/templates/${editing.id}`, payload);
      } else {
        await api.post("/auto-msg/templates", payload);
      }
      setShowModal(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Şablonu silmek istiyor musunuz?")) return;
    try {
      await api.delete(`/auto-msg/templates/${id}`);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    }
  };

  const handleDragEnd = async () => {
    if (dragItem.current == null || dragOverItem.current == null) return;
    if (dragItem.current === dragOverItem.current) return;
    const next = [...templates];
    const [item] = next.splice(dragItem.current, 1);
    next.splice(dragOverItem.current, 0, item);
    dragItem.current = null;
    dragOverItem.current = null;
    setTemplates(next);
    try {
      await api.put("/auto-msg/templates-reorder", {
        templateIds: next.map((t) => t.id)
      });
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      load();
    }
  };

  if (loading) {
    return <div className="d-flex justify-content-center py-5"><Spinner animation="border" /></div>;
  }

  return (
    <div className="py-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h4 className="mb-1">Hazır Mesaj Şablonları</h4>
          <p className="text-muted mb-0">
            Toplu mesaj ekranında seçilebilir. Değişkenler: {"{{first_uye_name}}"}, {"{{uye_sehir}}"}, {"{{uye_sehir_da}}"}
          </p>
        </div>
        <Button variant="primary" onClick={openNew}>
          <FontAwesomeIcon icon={faPlus} className="me-2" /> Yeni Şablon
        </Button>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <Card border="light" className="shadow-sm">
        <Card.Body className="p-0">
          <Table responsive hover className="align-middle mb-0">
            <thead>
              <tr>
                <th>#</th>
                <th>Başlık</th>
                <th>İçerik</th>
                <th>Tip</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {templates.length === 0 && (
                <tr><td colSpan={5} className="text-center text-muted py-4">Şablon yok</td></tr>
              )}
              {templates.map((t, index) => (
                <tr
                  key={t.id}
                  draggable
                  onDragStart={() => { dragItem.current = index; }}
                  onDragEnter={() => { dragOverItem.current = index; }}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => e.preventDefault()}
                  style={{ cursor: "grab" }}
                >
                  <td>{index + 1}</td>
                  <td className="fw-bold">{t.title}</td>
                  <td className="text-truncate" style={{ maxWidth: 320 }}>{t.text || "—"}</td>
                  <td><Badge bg="secondary">{t.mediaType}</Badge></td>
                  <td className="text-end">
                    <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEdit(t)}>
                      <FontAwesomeIcon icon={faEdit} />
                    </Button>
                    <Button size="sm" variant="outline-danger" onClick={() => remove(t.id)}>
                      <FontAwesomeIcon icon={faTrash} />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>{editing ? "Şablonu Düzenle" : "Yeni Şablon"}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Başlık</Form.Label>
            <Form.Control value={title} onChange={(e) => setTitle(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Metin</Form.Label>
            <Form.Control as="textarea" rows={4} value={text} onChange={(e) => setText(e.target.value)} />
          </Form.Group>
          <Form.Group className="mb-3">
            <Form.Label>Medya tipi</Form.Label>
            <Form.Select value={mediaType} onChange={(e) => setMediaType(e.target.value)}>
              <option value="text">Yazı</option>
              <option value="image">Fotoğraf</option>
              <option value="video">Video</option>
              <option value="audio">Ses</option>
            </Form.Select>
          </Form.Group>
          {mediaType !== "text" && (
            <Form.Group>
              <Form.Label>Dosya</Form.Label>
              <Form.Control
                type="file"
                accept={mediaType === "image" ? "image/*" : mediaType === "video" ? "video/*" : "audio/*"}
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>İptal</Button>
          <Button variant="primary" disabled={saving} onClick={save}>
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
